import { Box, Center, Divider, Flex, VStack } from '@hover/blueprint';
import { usePrevious } from '@hover/blueprint/chakra';
import { breakpoints } from '@hover/blueprint/foundation';
import { AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, matchPath, useParams } from 'react-router';

import { IdeasSection } from 'src/modules/homeownerApp/components/Projects/Project/IdeasSection/IdeasSection';
import { MeasurementHeader } from 'src/modules/homeownerApp/components/Projects/Project/ScanDetailsModal';
import { SectionVisibilityWrapper } from 'src/modules/homeownerApp/components/Projects/Project/SectionVisibilityWrapper';
import { StartScanCTA } from 'src/modules/homeownerApp/components/Scan/StartScanCTA';
import {
  addUploadToProject,
  closeUploadSheetAfterUpload,
} from 'src/modules/homeownerApp/components/UploadImage';
import { hasExteriorPhoto } from 'src/modules/homeownerApp/components/Utils';
import {
  useAnchorScroll,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { ImperativeTrackingProvider } from 'src/modules/homeownerApp/hooks/useImperativeTracking';
import { PartnerProjectProvider } from 'src/modules/homeownerApp/hooks/usePartner';
import { useVariationEdits } from 'src/modules/homeownerApp/hooks/useVariationEdits';
import { HomeownerAppMenuNames } from 'src/modules/homeownerApp/providers/HomeownerAppProvider';
import { Feature } from 'src/shared/components/Feature';
import { FeatureFlag } from 'src/shared/constants';
import type {
  AiDisplayImage,
  GetProjectQuery,
  Maybe,
  Project as ProjectType,
} from 'src/shared/graphql/generated';
import { useGetProjectQuery } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useEffectOnMount,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { useIsMobileSize } from 'src/shared/hooks/useIsMobileSize';
import { Route } from 'src/shared/route';

import { FullscreenImage } from '../../FullscreenImage';
import type { NavItemSchema } from '../../Navbar';
import { ScanError } from '../../Scan/ScanError';
import { StackingHeaderProvider } from '../../StackingHeader';
import { NotFoundSplash } from '../../errors';

import { ConnectPro } from './ConnectPro';
import { ExploreServices } from './ExploreServices';
import { ProjectHeader } from './Header/ProjectHeader';
import { KeyMeasurements } from './KeyMeasurements';
import { MostRecentScan } from './MostRecentScan';
import {
  HeaderSection,
  PROJECT_DETAILS_PADDING,
  ProjectCardSection,
  ProjectCardWrapper,
} from './ProjectCardSection';
import { ProjectCarousel } from './ProjectCarousel';
import { ProjectDetailsNavigation } from './ProjectDetailsNavigation';
import { ProjectSkeleton } from './ProjectSkeleton';
import { ProjectSwatch } from './ProjectSwatches';
import { ScanForMeasurements } from './ScanForMeasurements';
import {
  useAttachableScans,
  useAvailableProjectCards,
  useGetFailedProjectScans,
} from './hooks';

const MOBILE_NAV_BAR_HEIGHT = 74;
const TABLET_NAV_BAR_HEIGHT = 96;

export const inferProjectAreasFromSpaceTypes = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): string[] => {
  if (!assets) return [];
  return [
    ...new Set(
      assets
        .filter(({ spaceType }) => !!spaceType)
        .map(({ spaceType }) => spaceType as string),
    ),
  ];
};

const getProjectImages = (
  assets: Maybe<AiDisplayImage[]> | undefined,
): AiDisplayImage[] => {
  const images: AiDisplayImage[] = [];
  assets?.forEach(asset => {
    images.push(asset);
  }, []);

  return images;
};

const PROJECT_DETAILS_ROUTES = [
  Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
  Route.HOMEOWNER_APP_PROJECT.path,
];

const HEADER_BACKGROUND = 'neutral.875';

export interface ProjectProps {
  data: GetProjectQuery | undefined;
  loading: boolean;
  projectId: string;
}

export const Project = ({ data, loading, projectId }: ProjectProps) => {
  const location = useLocation();
  const match = matchPath<{ projectId: string }>(
    location.pathname,
    PROJECT_DETAILS_ROUTES,
  );
  const isOnProjectDetails =
    !!match && match.isExact && match.params.projectId !== 'new';
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const projectNameVisibilityHandler = useRef<HTMLDivElement>(null);
  const comingSoonRef = useRef<HTMLDivElement>(null);
  const materialsRef = useRef<HTMLDivElement>(null);
  const measurementsRef = useRef<HTMLDivElement>(null);
  const ideasRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const projectHeaderRef = useRef<HTMLDivElement>(null);
  const isMobileSize = useIsMobileSize();
  const [currentImage, setCurrentImage] = useState<AiDisplayImage>();
  const [newImageId, setNewImageId] = useState<number>();
  const [recentlyViewedSection, setRecentlyViewedSection] = useState<string>();
  const { currentMenu, imageFileToUpload } = useHomeownerAppContext();

  const sectionToRef: Record<string, React.RefObject<HTMLDivElement>> = useMemo(
    () => ({
      comingSoon: comingSoonRef,
      measurements: measurementsRef,
      ideas: ideasRef,
      materials: materialsRef,
      services: servicesRef,
      help: helpRef,
    }),
    [comingSoonRef, materialsRef, measurementsRef, servicesRef, helpRef],
  );

  const scrollTop = -(isMobileSize
    ? MOBILE_NAV_BAR_HEIGHT
    : TABLET_NAV_BAR_HEIGHT);

  const scrollToRef = useAnchorScroll(scrollTop);
  const { setCurrentMenu, user } = useHomeownerAppContext();

  const project = data?.project as ProjectType | undefined;

  useEffect(() => {
    if (isOnProjectDetails && project?.id) {
      closeUploadSheetAfterUpload(true);
      addUploadToProject(project);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnProjectDetails, project?.id]);

  useEffectOnMount(() => {
    if (location.hash) {
      scrollToRef(sectionToRef[location.hash.slice(1)]);
    } else {
      window.scrollTo(0, 0);
    }
  });

  const addables: AiDisplayImage[] = useMemo(
    () => project?.addables || [],
    [project],
  );
  const images = useMemo(() => getProjectImages(addables) || [], [addables]);
  const isExteriorProject = images?.[0]?.spaceType === 'exterior';
  const hasImages = !!addables?.length;

  const projectFailedScan = useGetFailedProjectScans(project);
  const showScanError = !!projectFailedScan;

  const allVariationEdits = useVariationEdits(addables);
  const materialEdits = Object.values(allVariationEdits).sort((a, b) => {
    if (a.kind === 'paint' && b.kind === 'material') return -1;
    if (a.kind === 'material' && b.kind === 'paint') return 1;
    return 0;
  });

  const attachedScan = project?.jobs?.[0];
  const attachableScans = useAttachableScans();
  const mostRecentAttachableScan = attachableScans[0];
  const hasExterior = hasExteriorPhoto(
    data?.project?.addables as Maybe<AiDisplayImage[]>,
  );
  const isHomeowner = !!user?.isHomeowner;
  const showAttachScan =
    hasExterior && mostRecentAttachableScan && !attachedScan;

  const {
    showComingSoon,
    scanForMeasurementsConfig,
    connectWithAProConfig,
    exploreServicesConfigs,
  } = useAvailableProjectCards({ project, isExteriorProject });

  const prevAddables = usePrevious(addables);

  useEffect(() => {
    if (!prevAddables?.length || prevAddables?.length === addables?.length)
      return;

    const prev = prevAddables?.map(({ id }) => id);
    const current = addables.map(({ id }) => id);
    const diff = current.filter(id => !prev.includes(id));

    setNewImageId(diff[0]);
  }, [addables, prevAddables]);

  // Scroll to new image when it's added
  useEffect(() => {
    if (!newImageId || currentMenu || imageFileToUpload) return;

    document
      ?.querySelector(`[data-id="${newImageId}"]`)
      ?.closest('[data-testid="peeking-carousel-element"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    setNewImageId(undefined);
  }, [currentMenu, newImageId, imageFileToUpload]);

  if (loading) return <ProjectSkeleton />;
  if (!project) return <NotFoundSplash />;

  // Only show coming soon module if it's enabled and measurement is not enabled
  // or if the project is interior only
  const ANCHOR_SECTION_TO_IS_VISIBLE: Record<string, boolean> = {
    comingSoon: showComingSoon && !hasExterior,
    materials: !!materialEdits.length,
    ideas: true,
    services: isHomeowner && !!exploreServicesConfigs.length,
    help: isHomeowner && !!connectWithAProConfig,
    measurements: isHomeowner && !!attachableScans.length && hasExterior,
    startScan: isHomeowner && !showAttachScan && !attachableScans.length,
  };

  const TRACK_ID = 'project-jump-list';

  const getJumpLinks = () => {
    const links: NavItemSchema[] = [
      {
        name: 'measurements',
        trackId: TRACK_ID,
        path: '#measurements',
        translatedName: t(`navbar.measurements`),
      },
      {
        name: 'ideas',
        trackId: TRACK_ID,
        path: '#ideas',
        translatedName: t(`navbar.ideas`),
      },
      {
        name: 'materials',
        trackId: TRACK_ID,
        path: '#materials',
        translatedName: t(`navbar.materials`),
      },
      {
        name: 'services',
        trackId: TRACK_ID,
        path: '#services',
        translatedName: t(`navbar.services`),
      },
      {
        name: 'help',
        trackId: TRACK_ID,
        path: '#help',
        translatedName: connectWithAProConfig?.jumpLink
          ? connectWithAProConfig?.jumpLink
          : t(`navbar.help`),
      },
    ] as const satisfies NavItemSchema[];

    if (!attachedScan) {
      [links[0], links[1]] = [links[1], links[0]];
    }

    return links;
  };

  const availableNavLinks = getJumpLinks().filter(
    link => !!ANCHOR_SECTION_TO_IS_VISIBLE[link.name],
  );

  const handleMoreActionsClick = () => {
    setCurrentMenu(HomeownerAppMenuNames.PROJECT_MORE_ACTIONS);
  };

  const handleNavButtonClick = (sectionName: string) => {
    scrollToRef(sectionToRef[sectionName]);
  };
  const materialTitle = t('projects.swatch.material.title');

  const comingSoon = ANCHOR_SECTION_TO_IS_VISIBLE.comingSoon && (
    <Flex px={PROJECT_DETAILS_PADDING}>
      <SectionVisibilityWrapper
        ref={comingSoonRef}
        sectionId="comingSoon"
        setSectionInView={setRecentlyViewedSection}
        w="1"
      >
        <ScanForMeasurements
          config={scanForMeasurementsConfig}
          isExteriorProject={isExteriorProject}
          projectId={projectId}
        />
      </SectionVisibilityWrapper>
    </Flex>
  );

  const sectionStartScan = ANCHOR_SECTION_TO_IS_VISIBLE.startScan && (
    <Feature name={FeatureFlag.Designer.START_SCAN}>
      <StartScanCTA />
    </Feature>
  );

  const sectionMeasurements = ANCHOR_SECTION_TO_IS_VISIBLE.measurements && (
    <SectionVisibilityWrapper
      ref={measurementsRef}
      sectionId="measurements"
      setSectionInView={setRecentlyViewedSection}
    >
      {/* Scan Error */}
      <Feature name={FeatureFlag.Designer.START_SCAN}>
        {isHomeowner && showScanError && (
          <ScanError projectId={projectId} scan={projectFailedScan} />
        )}
      </Feature>
      {showAttachScan && (
        <MostRecentScan projectId={projectId} scan={mostRecentAttachableScan} />
      )}
      {attachedScan && !showScanError && (
        <>
          <MeasurementHeader
            projectId={Number(projectId)}
            scan={attachedScan}
          />
          <KeyMeasurements
            jobId={attachedScan.id}
            projectId={Number(projectId)}
          />
          <Divider
            display={{ tablet: 'none' }}
            mb={300}
            mt={500}
            mx={PROJECT_DETAILS_PADDING}
            width="auto"
          />
        </>
      )}
    </SectionVisibilityWrapper>
  );

  const sectionScans = (ANCHOR_SECTION_TO_IS_VISIBLE.comingSoon ||
    ANCHOR_SECTION_TO_IS_VISIBLE.startScan ||
    ANCHOR_SECTION_TO_IS_VISIBLE.measurements) && (
    <Flex
      data-testid="project-scans-section"
      flexDirection="column"
      gap={950}
      maxWidth={breakpoints.width500}
      mt="0 !important"
      pb={950}
      width="100%"
    >
      {comingSoon}
      {sectionStartScan}
      {sectionMeasurements}
    </Flex>
  );

  const sectionIdeas = ANCHOR_SECTION_TO_IS_VISIBLE.ideas && (
    <Flex
      data-testid="project-ideas-section"
      flexDirection="column"
      gap={950}
      pb={950}
      ref={ideasRef}
      width="100%"
    >
      <AnimatePresence mode="popLayout">
        <SectionVisibilityWrapper
          animate={{ opacity: 1 }}
          display="block"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          key={currentImage?.id}
          m={0}
          sectionId="ideas"
          setSectionInView={setRecentlyViewedSection}
        >
          <IdeasSection image={currentImage} projectId={projectId} />
        </SectionVisibilityWrapper>
      </AnimatePresence>
    </Flex>
  );

  const sectionMaterials = ANCHOR_SECTION_TO_IS_VISIBLE.materials && (
    <Flex
      data-testid="project-materials-section"
      flexDirection="column"
      gap={950}
      maxWidth={breakpoints.width500}
      pb={950}
      width="100%"
    >
      <SectionVisibilityWrapper
        display="flex"
        flexDir="column"
        gap={800}
        ref={materialsRef}
        sectionId="materials"
        setSectionInView={setRecentlyViewedSection}
        w="1"
      >
        <VStack alignItems="flex-start">
          <HeaderSection
            subtitle={t('projects.swatch.material.subtitle')}
            title={materialTitle}
          />
          <ProjectCardSection>
            {materialEdits.map(edit => (
              <ProjectCardWrapper key={edit.uid}>
                <ProjectSwatch
                  edit={edit}
                  projectId={projectId}
                  title={materialTitle}
                />
              </ProjectCardWrapper>
            ))}
          </ProjectCardSection>
        </VStack>
      </SectionVisibilityWrapper>
    </Flex>
  );

  const sectionServices = ANCHOR_SECTION_TO_IS_VISIBLE.services && (
    <SectionVisibilityWrapper
      ref={servicesRef}
      sectionId="services"
      setSectionInView={setRecentlyViewedSection}
    >
      <ExploreServices
        projectId={projectId}
        services={exploreServicesConfigs.map(
          ({ config: serviceConfig }) => serviceConfig,
        )}
      />
    </SectionVisibilityWrapper>
  );

  const sectionHelp = ANCHOR_SECTION_TO_IS_VISIBLE.help &&
    connectWithAProConfig && (
      <SectionVisibilityWrapper
        ref={helpRef}
        sectionId="help"
        setSectionInView={setRecentlyViewedSection}
      >
        <Box
          px={{ tablet: PROJECT_DETAILS_PADDING.tablet }}
          py={PROJECT_DETAILS_PADDING}
          width="100%"
        >
          <ConnectPro config={connectWithAProConfig} projectId={projectId} />
        </Box>
      </SectionVisibilityWrapper>
    );

  return (
    <StackingHeaderProvider
      height={{
        desktop: { expanded: 0, collapsed: 0 },
        base: { expanded: 80, collapsed: 80 },
      }}
    >
      <Center height="100%">
        <VStack width="100%">
          <VStack
            background={HEADER_BACKGROUND}
            sx={{
              transition: 'background-color 1s ease',
            }}
            width={1}
          >
            {/* Header */}
            <ProjectHeader
              background={HEADER_BACKGROUND}
              data-testid="project-header"
              hasImages={hasImages}
              id={project.id}
              name={project.name}
              onMoreActionsClick={handleMoreActionsClick}
              ref={projectHeaderRef}
            />
            {/* Hero */}
            <FullscreenImage
              hidePageContentsWhenAnimatingFromCustomize
              images={images}
              path={generatePath(
                Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path,
                { projectId, aiDisplayImageId: ':aiDisplayImageId' },
              )}
              returnPath={generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
                projectId,
              })}
            >
              <ProjectCarousel
                addables={addables}
                onActiveImageChange={setCurrentImage}
                pb={{
                  base: 0,
                  tablet: 900,
                }}
                projectId={projectId}
                projectNameVisibilityHandler={projectNameVisibilityHandler}
                width="100%"
              />
            </FullscreenImage>
          </VStack>
          {/*  Project Details Navigation */}
          <ProjectDetailsNavigation
            availableNavLinks={availableNavLinks}
            navRef={navRef}
            onNavButtonClick={handleNavButtonClick}
            overflow="unset"
            projectId={project.id}
            pt={{ base: 700, tablet: 0 }}
            px={PROJECT_DETAILS_PADDING}
            recentlyViewedSection={recentlyViewedSection}
          />
          {attachedScan ? (
            <>
              {sectionScans}
              {sectionIdeas}
            </>
          ) : (
            <>
              {sectionIdeas}
              {sectionScans}
            </>
          )}
          {sectionMaterials}
          <Flex
            flexDirection="column"
            gap={950}
            maxWidth={breakpoints.width500}
            pb={ANCHOR_SECTION_TO_IS_VISIBLE.help ? 'unset' : 950}
            width="100%"
          >
            {sectionServices}
            {sectionHelp}
          </Flex>
        </VStack>
      </Center>
    </StackingHeaderProvider>
  );
};

export const ProjectRoute = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { data, loading } = useGetProjectQuery({
    variables: {
      projectId: Number(projectId),
    },
  });

  return (
    <ImperativeTrackingProvider>
      <PartnerProjectProvider project={data?.project}>
        <Project data={data} loading={loading} projectId={projectId} />
      </PartnerProjectProvider>
    </ImperativeTrackingProvider>
  );
};
